import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';

import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import * as UserSelectors from 'app/selectors/userSelectors.ts';
import { selectedLanguage } from '@/features/language/signals.ts';
import { I18nLanguage } from '@/i18n/types/i18nLanguage.ts';
import * as ScenarioActions from 'app/actions/scenarios/scenarioAction.ts';
import type { ScenarioContextDto, ScenarioDto, VariableSettingsDto, VariableDto } from '@/api-gen/api.schemas';
import { Llm } from '@/api-gen/api.schemas';

import { ScenarioSettingsLanguageVals } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/types.ts';
import { useValidationSchema } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioGeneralSettings/validation.ts';
import type { ScenarioSettingsFormProps } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioGeneralSettings/types.ts';
import type { FieldErrors } from 'react-hook-form';
import {
  mapScenarioFormToAddCommand,
  mapScenarioFormToUpdateCommand
} from 'app/utils/scenarioSettingsMapper.ts';
import logger from 'app/utils/logger.ts';

import type { EvaluationProtocolElementDto as OrvalEvaluationProtocolElementDto } from '@/api-gen/api.schemas';

export type ScenarioSettingsType = Omit<
  Pick<
    ScenarioDto,
    | 'id'
    | 'name'
    | 'description'
    | 'langCode'
    | 'scenarioContextIntro'
    | 'playScenarioContextOn'
    | 'context'
    | 'isLlmEnabled'
    | 'isDecisionSupportEnabled'
    | 'notesEnabled'
    | 'llm'
    | 'useStreaming'
    | 'maxTokens'
    | 'variables'
    | 'scenarioPersonas'
    | 'companyId'
    | 'sharedCompanies'
    | 'isPublished'
    | 'evaluationProtocolElements'
    | 'notesEvaluationProtocolElements'
  >,
  'evaluationProtocolElements' | 'variables'
> & {
  evaluationProtocolElements?: OrvalEvaluationProtocolElementDto[] | null;
  notesEvaluationProtocolElements?: OrvalEvaluationProtocolElementDto[] | null;
  variables?: VariableSettingsDto[] | null;
  createSharedScenario?: boolean;
  speed?: number; // Voice speed factor (0.7 to 1.2)
  // Voice fields are no longer on ScenarioDto; managed at Persona level
  // Kept for backwards compatibility with form state
  sex?: any;
  voiceName?: string | null;
  voiceId?: string | null;
  ttsProvider?: any;
};

const defaultContext: ScenarioContextDto = {
  id: 0,
  facts: [],
  locations: []
};

function mapVariablesToSettings(variables?: (VariableDto | null)[] | null): VariableSettingsDto[] {
  if (!variables) {
    return [];
  }

  return variables
    .filter((variable): variable is VariableDto => variable != null)
    .map((variable) => ({
      id: variable.id ?? null,
      name: variable.name ?? null,
      deletedValueIds: [],
      values:
        variable.values?.map((value) => ({
          id: value?.id ?? null,
          value: value?.value ?? null,
          companyId: value?.companyId ?? null
        })) ?? []
    }));
}

/**
 * Maps the user's selected language (I18nLanguage) to the corresponding ScenarioSettingsLanguageVals
 */
function mapUserLanguageToScenarioLanguage(userLanguage: I18nLanguage): ScenarioSettingsLanguageVals {
  switch (userLanguage) {
    case I18nLanguage.Norwegian:
      return ScenarioSettingsLanguageVals.Norwegian; // nb-NO
    case I18nLanguage.German:
      return ScenarioSettingsLanguageVals.German; // de-DE
    case I18nLanguage.Dutch:
      return ScenarioSettingsLanguageVals.Dutch; // nl-NL
    case I18nLanguage.EnglishUk:
      return ScenarioSettingsLanguageVals.EnglishUk; // en-GB
    case I18nLanguage.English:
    default:
      return ScenarioSettingsLanguageVals.EnglishUk; // en-GB as default
  }
}

const defaultFormValues: ScenarioSettingsType = {
  id: 0,
  name: '',
  description: '',
  scenarioContextIntro: null,
  playScenarioContextOn: false,
  context: defaultContext,
  scenarioPersonas: [],
  isLlmEnabled: true,
  isDecisionSupportEnabled: false,
  notesEnabled: false,
  createSharedScenario: false,
  llm: Llm.Gemini2_5Flash,
  useStreaming: false,
  maxTokens: 25,
  langCode: mapUserLanguageToScenarioLanguage(selectedLanguage.value),
  speed: 1.0, // Default voice speed
  isPublished: true, // Default published state
  evaluationProtocolElements: [],
  notesEvaluationProtocolElements: [],
  variables: []
};

function getFormDefaultData(scenario?: ScenarioDto) {
  // Get the user's selected language and map it to a scenario language code
  const userLanguage = selectedLanguage.value;
  const defaultLangCode = mapUserLanguageToScenarioLanguage(userLanguage);

  const sharedCompanies = scenario?.sharedCompanies ?? [];

  return scenario != null
    ? {
        id: scenario.id,
        name: scenario.name,
        description: scenario.description,
        scenarioContextIntro: scenario.scenarioContextIntro ?? '',
        playScenarioContextOn: scenario.playScenarioContextOn ?? false,
        langCode: scenario.langCode,
        isLlmEnabled: scenario.isLlmEnabled ?? true,
        isDecisionSupportEnabled: scenario.isDecisionSupportEnabled ?? false,
        notesEnabled: scenario.notesEnabled ?? false,
        llm: scenario.llm == null ? Llm.Gemini2_5Flash : scenario.llm,
        useStreaming: scenario.useStreaming == null ? false : scenario.useStreaming,
        maxTokens: scenario.maxTokens ?? 25,
        context: scenario.context,
        speed: (scenario as any).speed ?? 1.0, // Use existing speed or default to 1.0
        variables: mapVariablesToSettings(scenario.variables),
        isPublished: scenario.isPublished ?? false,
        sharedCompanies,
        evaluationProtocolElements: (scenario.evaluationProtocolElements as unknown as OrvalEvaluationProtocolElementDto[]) ?? [], // Added
        notesEvaluationProtocolElements: (scenario.notesEvaluationProtocolElements as unknown as OrvalEvaluationProtocolElementDto[]) ?? [],
        scenarioPersonas: scenario.scenarioPersonas ?? [], // Ensure scenarioPersonas is always an array
        companyId: scenario.companyId
      }
    : {
        ...defaultFormValues,
        // Use the mapped language code from user's selected language
        langCode: defaultLangCode,
        maxTokens: 25,
        sharedCompanies: []
      };
}

export interface UseScenarioFormOptions {
  onClose: ScenarioSettingsFormProps['onClose'];
  scenarioId?: ScenarioSettingsFormProps['scenarioId'];
  onValidationError?: (errors: FieldErrors<ScenarioSettingsType>) => void;
}

const useScenarioForm = (
  onClose: ScenarioSettingsFormProps['onClose'],
  scenarioId?: ScenarioSettingsFormProps['scenarioId'],
  onValidationError?: (errors: FieldErrors<ScenarioSettingsType>) => void
) => {
  const scenario = useSelector(ScenarioSelectors.getScenarioById(scenarioId));
  const currentUser = useSelector(UserSelectors.CurrentUserSelector);
  const isScenarioLoadedForSubmit = scenarioId == null || scenario != null;

  const defaultValues = useMemo(() => {
    return getFormDefaultData(scenario);
  }, [scenario]);

  const methods = useForm<ScenarioSettingsType>({
    resolver: zodResolver(useValidationSchema()),
    defaultValues: defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldUnregister: false
  });

  useEffect(() => {
    methods.reset(defaultValues);
    methods.register('id');
  }, [defaultValues, methods]);

  const dispatch = useDispatch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSubmit = useCallback(
    methods.handleSubmit(
      (formData: ScenarioSettingsType) => {
        logger.log(formData);

        if (!isScenarioLoadedForSubmit) {
          logger.warn('Prevented scenario settings submit before scenario data finished loading.');
          return;
        }

        if (scenarioId != null) {
          const updateCommand = mapScenarioFormToUpdateCommand(formData, {
            scenarioId,
            currentCompanyId: currentUser?.companyId ?? null
          });
          logger.log(updateCommand);
          dispatch(ScenarioActions.updateScenarioDetails(updateCommand));
        } else {
          const addCommand = mapScenarioFormToAddCommand(formData, {
            currentCompanyId: currentUser?.companyId ?? null
          });
          logger.log(addCommand);
          dispatch(ScenarioActions.addScenario(addCommand));
        }

        onClose();
      },
      (errors) => {
        onValidationError?.(errors);
      }
    ),
    [methods.handleSubmit, dispatch, scenarioId, onClose, currentUser, onValidationError, isScenarioLoadedForSubmit]
  );

  return { methods, onSubmit, isScenarioLoadedForSubmit };
};

export default useScenarioForm;
